import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import SockJS from 'sockjs-client';
import { Client, Stomp, StompSubscription } from '@stomp/stompjs';

// Define the structure of the message from the backend
export interface SignJobUpdate {
  jobId: string;
  status: 'PENDING' | 'COMPLETED' | 'FAILED';
  message?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SignNotificationService {
  private stompClient!: Client;
  // Subject to emit updates to components
  private jobUpdatesSubject = new Subject<SignJobUpdate>();
  private connectionSubject = new Subject<boolean>();
  private isConnected = false;
  private pendingSubscriptions: Map<string, () => void> = new Map();
  private activeSubscriptions: Map<string, StompSubscription> = new Map();
  private visibilityChangeHandler?: () => void;

  constructor() {
    this.initializeStompClient();
    this.setupVisibilityHandler();
  }

  // Expose updates as an Observable
  public get jobUpdates$(): Observable<SignJobUpdate> {
    return this.jobUpdatesSubject.asObservable();
  }

  // Expose connection status as an Observable
  public get connectionStatus$(): Observable<boolean> {
    return this.connectionSubject.asObservable();
  }

  private initializeStompClient() {
    const socketUrl = environment.apiBase + '/ws/notifications';
    console.log('🔌 Initializing SockJS/STOMP client for:', socketUrl);

    this.stompClient = Stomp.over(() => new SockJS(socketUrl));
    
    // Configure STOMP behavior
    this.stompClient.reconnectDelay = 5000; // Reconnect after 5 seconds
    this.stompClient.heartbeatIncoming = 10000; // Expect heartbeat every 10 seconds
    this.stompClient.heartbeatOutgoing = 10000; // Send heartbeat every 10 seconds

    this.stompClient.onConnect = () => {
      console.log('✅ STOMP Connected');
      this.isConnected = true;
      this.connectionSubject.next(true);
      
      // Process pending subscriptions
      this.processPendingSubscriptions();
    };

    this.stompClient.onDisconnect = () => {
      console.log('❌ STOMP Disconnected');
      this.isConnected = false;
      this.connectionSubject.next(false);
      this.activeSubscriptions.clear();
    };

    this.stompClient.onStompError = (frame) => {
      console.error('🚨 STOMP Error:', frame.headers['message']);
      console.error('Details:', frame.body);
      this.connectionSubject.next(false);
    };

    this.stompClient.activate();
  }

  private setupVisibilityHandler(): void {
    this.visibilityChangeHandler = () => {
      if (document.visibilityState === 'visible') {
        console.log('👁️ Page became visible, checking WebSocket connection...');
        
        // Reconnect if not connected
        if (!this.isConnected && this.stompClient) {
          console.log('🔄 WebSocket disconnected, reconnecting...');
          this.stompClient.activate();
        }
      }
    };
    
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  private processPendingSubscriptions() {
    if (this.pendingSubscriptions.size > 0) {
      console.log(`📋 Processing ${this.pendingSubscriptions.size} pending subscriptions...`);
      this.pendingSubscriptions.forEach((subscribeFn, jobId) => {
        subscribeFn();
      });
      this.pendingSubscriptions.clear();
    }
  }

  // Subscribe to a specific job topic
  public subscribeToJob(jobId: string): void {
    if (this.activeSubscriptions.has(jobId)) {
      console.log(`⚠️ Already subscribed to job: ${jobId}`);
      return;
    }

    const doSubscribe = () => {
      const topic = `/topic/signing/${jobId}`;
      console.log(`📡 Subscribing to topic: ${topic}`);

      const subscription = this.stompClient.subscribe(topic, (message) => {
        console.log('📨 Received WS Message (raw):', message);
        console.log('📨 Message Body:', message.body);
        console.log('📨 Message Body Type:', typeof message.body);
        
        try {
          let update: SignJobUpdate;
          
          // Try to parse as JSON first
          try {
            update = JSON.parse(message.body) as SignJobUpdate;
            console.log('✅ Parsed as JSON:', update);
          } catch (jsonError) {
            // If JSON parsing fails, treat as plain string status
            console.log('⚠️ Not JSON, treating as plain string. Value:', message.body);
            const status = message.body.trim();
            
            // Convert plain string status to SignJobUpdate format
            update = {
              jobId: jobId,
              status: status as 'PENDING' | 'COMPLETED' | 'FAILED',
              message: status === 'COMPLETED' 
                ? 'Dokument bol úspešne podpísaný!' 
                : status === 'FAILED' 
                  ? 'Podpisovanie zlyhalo.'
                  : 'Podpis prebieha...'
            };
            console.log('✅ Converted to update object:', update);
          }
          
          this.jobUpdatesSubject.next(update);
        } catch (e) {
          console.error('❌ Error processing WS message:', e);
          console.error('❌ Message that failed:', message);
        }
      });

      this.activeSubscriptions.set(jobId, subscription);
    };

    if (!this.isConnected) {
      console.log(`⏳ STOMP not connected yet. Queueing subscription for job: ${jobId}`);
      this.pendingSubscriptions.set(jobId, doSubscribe);
    } else {
      doSubscribe();
    }
  }

  // Unsubscribe from a specific job
  public unsubscribeFromJob(jobId: string): void {
    const subscription = this.activeSubscriptions.get(jobId);
    if (subscription) {
      subscription.unsubscribe();
      this.activeSubscriptions.delete(jobId);
      console.log(`🔕 Unsubscribed from job: ${jobId}`);
    }
    
    // Also remove from pending if it's there
    if (this.pendingSubscriptions.has(jobId)) {
      this.pendingSubscriptions.delete(jobId);
    }
  }

  // Disconnect the WebSocket client
  public disconnect(): void {
    if (this.stompClient && this.isConnected) {
      console.log('🔌 Disconnecting WebSocket...');
      this.stompClient.deactivate();
    }
    
    // Remove visibility listener
    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
      this.visibilityChangeHandler = undefined;
    }
  }

  // Get current connection status (for debugging)
  public isWebSocketConnected(): boolean {
    return this.isConnected;
  }

  // Get list of active subscriptions (for debugging)
  public getActiveSubscriptions(): string[] {
    return Array.from(this.activeSubscriptions.keys());
  }

  // TEST HELPER: Simulate receiving a WebSocket message (for testing without backend)
  public simulateJobUpdate(jobId: string, status: 'PENDING' | 'COMPLETED' | 'FAILED', message?: string): void {
    console.log('🧪 SIMULATING WebSocket message:', { jobId, status, message });
    const update: SignJobUpdate = {
      jobId,
      status,
      message: message || `Test message: ${status}`
    };
    this.jobUpdatesSubject.next(update);
  }
}